import type { CSSProperties, FC, ReactNode } from "react";
import { Toaster as SonnerToaster, toast as sonner } from "sonner";
import { IconNotification } from "../components/application/notifications/notifications";

type Tone = "success" | "error" | "warning" | "brand" | "default";

/**
 * Toast host (Untitled UI PRO "notifications" rendered through sonner). Mounted once by AppProviders.
 * Brand font + RTL content (see globals.css portal rules), top-center so it never hides the inline-start sidebar or the bottom action bar on phones.
 */
export const Toaster = () => (
    <SonnerToaster
        // Positioning stays LTR: sonner's RTL mobile rule shifts the stack off-screen. Content sets dir="rtl" itself.
        dir="ltr"
        position="top-center"
        gap={12}
        visibleToasts={4}
        toastOptions={{ unstyled: true, duration: 5000 }}
        style={{ "--width": "min(25rem, calc(100vw - 2rem))" } as CSSProperties}
    />
);

interface NotifyOptions {
    description?: ReactNode;
    icon?: FC<{ className?: string }>;
    /** Optional action, e.g. «مشاهدهٔ رسید». */
    action?: { label: string; onPress: () => void };
    duration?: number;
}

const show = (tone: Tone, title: ReactNode, options: NotifyOptions = {}) =>
    sonner.custom(
        (id) => (
            <IconNotification
                title={title}
                description={options.description ?? ""}
                color={tone}
                icon={options.icon}
                hideDismissLabel
                confirmLabel={options.action?.label}
                onConfirm={() => {
                    options.action?.onPress();
                    sonner.dismiss(id);
                }}
                onClose={() => sonner.dismiss(id)}
            />
        ),
        { duration: options.duration },
    );

/** Persian toasts: `notify.success("کد ساخته شد", { description: "..." })`. */
export const notify = {
    success: (title: ReactNode, options?: NotifyOptions) => show("success", title, options),
    error: (title: ReactNode, options?: NotifyOptions) => show("error", title, { duration: 8000, ...options }),
    warning: (title: ReactNode, options?: NotifyOptions) => show("warning", title, options),
    info: (title: ReactNode, options?: NotifyOptions) => show("brand", title, options),
    dismiss: (id?: string | number) => sonner.dismiss(id),
};
